import { useEffect, useRef, type ComponentType } from 'react';

import { BLINKET_URL } from 'settings';
import TopPage from 'v2/components/Shared/TopPage';
import { getProductTypeTitle } from 'v2/helpers/productTypeTitle';
import { usePostMessage } from 'v2/hooks/usePostMessage';
import { PostMessageAction, PostMessageSource } from 'v2/interfaces/post-message';
import { EProductType } from 'v2/interfaces/product';
import eduzzAccountsService from 'v2/services/eduzzAccount';

import { Icon } from '@eduzz/ui-antd-theme';

import AIButtonRedirect from './AIButtonRedirect';
import { ButtonRedirect } from './ButtonRedirect';
import { useWizardState } from '../../../contexts/WizardStateContext';

type IconComponent = ComponentType<{ className?: string; size?: number | string }>;

type ProductItem = {
  id: string;
  title: string;
  subtitle: string;
  type: EProductType;
  icon: IconComponent;
  betaModule?: string;
  isNew?: boolean;
  route?: string;
  redirect?: string;
};

const products: ProductItem[] = [
  {
    id: 'new-info-product-button',
    title: 'Digital',
    subtitle: 'Cadastre um infoproduto. E-books, cursos online, planilhas… ',
    icon: Icon.MonitorPlay,
    type: EProductType.DIGITAL,
    route: 'digital'
  },
  {
    id: 'new-subscription-button',
    title: 'Assinatura',
    subtitle: 'Crie planos de assinatura e garanta receita recorrente com cobranças automáticas.',
    type: EProductType.SUBSCRIPTION,
    icon: Icon.CalendarSync,
    route: 'subscription'
  },
  {
    id: 'new-physical-product-button',
    title: 'Produto físico para envio',
    subtitle: 'Livros, apostilas, brindes, premiações, objetos com frete...',
    icon: Icon.Package,
    type: EProductType.PHYSICAL,
    route: 'physical'
  },
  {
    id: 'new-jobzz-product-button',
    icon: Icon.Users,
    title: 'Serviço online',
    subtitle: 'Cadastre suas consultorias dedicadas, mentorias, coaching, bate-papo...',
    type: EProductType.SERVICE,
    route: 'service'
  },
  {
    id: 'new-blinket-product-button',
    icon: Icon.Ticket,
    title: 'Eventos',
    subtitle: 'Congressos, workshops, shows, eventos presenciais, mentorias em grupo, palestras...',
    type: EProductType.TICKET,
    redirect: `${BLINKET_URL}/event?utm_source=orbita&utm_medium=newproduct`
  }
];

function StepOne() {
  const { setSelected, nextStep } = useWizardState();
  const sendPostMessage = usePostMessage();
  const hasChosenTypeRef = useRef(false);

  useEffect(() => {
    onRouteChange();
  });

  useEffect(() => {
    return () => {
      if (!hasChosenTypeRef.current) {
        sendPostMessage({
          source: PostMessageSource.PRODUCT_MANAGER,
          action: PostMessageAction.PRODUCT_TYPE_DISMISSED,
          data: { reason: 'dismissed' }
        });
      }
    };
  }, [sendPostMessage]);

  const onRouteChange = () => {
    const fullPathname = window.location.pathname.split('/');
    const pathName = fullPathname[fullPathname.length - 1];
    const product = products.find(p => p.route === pathName);
    if (product) onChooseType(product);
  };

  const onChooseType = async (selected: (typeof products)[0]) => {
    hasChosenTypeRef.current = true;
    sendPostMessage({
      source: PostMessageSource.PRODUCT_MANAGER,
      action: PostMessageAction.PRODUCT_TYPE_SELECTED,
      data: { product_type: selected.type, product_type_title: getProductTypeTitle(selected.type) }
    });

    if (selected.type === EProductType.TICKET) {
      window.open(selected.redirect, '_blank');
      return;
    }

    try {
      setSelected(selected.type);
      nextStep();
      if (!selected.type) return;
      const url = EProductType[selected.type]?.toLowerCase() as Lowercase<keyof typeof EProductType>;
      setUrlHistory(url);
    } catch (error) {}
  };

  const setUrlHistory = (url: Lowercase<keyof typeof EProductType>) => {
    const currentUrl = window.location.href;
    if (url && !currentUrl.includes(url)) window.history.replaceState(null, null, `${currentUrl}/${url}`);
  };

  const hasPlansBetaModule = eduzzAccountsService.verifyUserHasBetaModule('planos_assinatura');
  return (
    <div>
      <TopPage title='Novo produto' extra='Escolha o tipo de produto que deseja cadastrar.' />
      <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
        {products.map(product => {
          if (product.betaModule && !eduzzAccountsService.verifyUserHasBetaModule(product.betaModule)) return null;
          if (hasPlansBetaModule && product.type === EProductType.DIGITAL) product.title = 'Cobrança única';
          return <ButtonRedirect key={product.title} {...product} onClick={() => onChooseType(product)} />;
        })}
      </div>
      <AIButtonRedirect />
    </div>
  );
}

export default StepOne;
